import type {
  BrowserCommand,
  BrowserSessionSnapshot,
  BrowserTabSnapshot,
  DevTunnelRecord,
  EnvironmentId,
  ThreadId,
} from "@cmmd-forge/contracts";
import {
  createBrowserControlSequencer,
  type BrowserControlSequencer,
} from "@cmmd-forge/client-runtime";
import type { MutableRefObject } from "react";
import { useCallback, useEffect, useRef } from "react";
import { newCommandId } from "~/lib/utils";
import { runThreadExecutionIntentAction } from "~/lib/runThreadExecutionIntentAction";
import {
  resolveBrowserCommandExecutionPlan,
  resolveBrowserCommandRuntimeAvailability,
} from "./BrowserPreviewPanel.logic";
import { dispatchBrowserCommand } from "./BrowserPreviewPanel.commandDispatch";
import type { BrowserCommandRpcResult } from "./BrowserPreviewPanel.commandResult";

export interface BrowserCommandRunOptions {
  readonly visiblePending?: boolean;
  readonly reportError?: boolean;
  readonly timeoutMs?: number;
  readonly onError?: (error: unknown) => void;
  readonly onSnapshot?: (snapshot: BrowserSessionSnapshot) => void;
}

export interface UseBrowserRunCommandOptions<PendingCommandState> {
  readonly activeTabRef: MutableRefObject<BrowserTabSnapshot | null>;
  readonly beginPendingBrowserCommand: (
    commandType: BrowserCommand["type"] | null,
    timeoutMs: number | undefined,
  ) => PendingCommandState;
  readonly clearPendingBrowserCommand: (pending: PendingCommandState) => void;
  readonly commandGenerationRef: MutableRefObject<number>;
  readonly defaultTimeoutMs: number;
  readonly devTunnelRecordsRef: MutableRefObject<readonly DevTunnelRecord[]>;
  readonly environmentId: EnvironmentId;
  readonly projectId: number | null;
  readonly handleBrowserCommandError: (input: {
    readonly command: BrowserCommand;
    readonly commandGeneration: number;
    readonly error: unknown;
    readonly options: BrowserCommandRunOptions;
    readonly useNativeBrowserCommand: boolean;
  }) => boolean;
  readonly handleNativeBrowserSurfaceResult: Parameters<
    typeof dispatchBrowserCommand
  >[0]["onNativeResult"];
  readonly onServerCommandResult: (input: {
    readonly command: BrowserCommand;
    readonly commandGeneration: number;
    readonly onSnapshot: ((snapshot: BrowserSessionSnapshot) => void) | undefined;
    readonly result: BrowserCommandRpcResult;
  }) => boolean;
  readonly reportUnavailableBrowserCommand: (
    availability: ReturnType<typeof resolveBrowserCommandRuntimeAvailability>,
    shouldReportError: boolean,
  ) => boolean;
  readonly session: BrowserSessionSnapshot | null;
  readonly setBrowserRuntimeError: (error: string | null) => void;
  readonly threadId: ThreadId;
}

export function useBrowserRunCommand<PendingCommandState>(
  options: UseBrowserRunCommandOptions<PendingCommandState>,
) {
  const controlSequencerRef = useRef<BrowserControlSequencer | null>(null);
  useEffect(
    () => () => {
      controlSequencerRef.current?.dispose();
      controlSequencerRef.current = null;
    },
    [],
  );
  const {
    activeTabRef,
    beginPendingBrowserCommand,
    clearPendingBrowserCommand,
    commandGenerationRef,
    defaultTimeoutMs,
    devTunnelRecordsRef,
    environmentId,
    projectId,
    handleBrowserCommandError,
    handleNativeBrowserSurfaceResult,
    onServerCommandResult,
    reportUnavailableBrowserCommand,
    session,
    setBrowserRuntimeError,
    threadId,
  } = options;

  return useCallback(
    async (
      command: BrowserCommand,
      runOptions: BrowserCommandRunOptions = {},
    ): Promise<boolean> => {
      const nativeBrowser = window.desktopBridge?.nativeBrowser;
      const commandPlan = resolveBrowserCommandExecutionPlan({
        command,
        activeTabId: activeTabRef.current?.tabId,
        session,
        nativeBrowserCanCommand: Boolean(nativeBrowser?.command),
        devTunnelRecords: devTunnelRecordsRef.current,
        visiblePending: runOptions.visiblePending,
        reportError: runOptions.reportError,
        timeoutMs: runOptions.timeoutMs,
        defaultTimeoutMs,
      });
      const commandGeneration = commandGenerationRef.current;
      const pending = beginPendingBrowserCommand(
        commandPlan.visiblePendingType,
        runOptions.timeoutMs,
      );
      try {
        const result = await runThreadExecutionIntentAction(
          environmentId,
          {
            action: "browser",
            projectId,
            targetId: threadId,
            dispatchIdentity: newCommandId(),
          },
          {
            dispatch: async (api) => {
              const runtimeAvailability = resolveBrowserCommandRuntimeAvailability({
                commandRoute: commandPlan.commandRoute,
                hasBrowserRuntime: Boolean(api.browser),
              });
              if (
                reportUnavailableBrowserCommand(runtimeAvailability, commandPlan.shouldReportError)
              ) {
                return false;
              }
              if (commandPlan.shouldReportError) {
                setBrowserRuntimeError(null);
              }
              const controlSequencer =
                controlSequencerRef.current ?? createBrowserControlSequencer();
              controlSequencerRef.current = controlSequencer;
              return dispatchBrowserCommand({
                browserApi: api.browser,
                command,
                environmentId,
                commandPlan,
                controlSequencer,
                nativeCommand: nativeBrowser?.command,
                onNativeResult: handleNativeBrowserSurfaceResult,
                onServerResult: (commandResult) =>
                  onServerCommandResult({
                    command,
                    commandGeneration,
                    result: commandResult,
                    onSnapshot: runOptions.onSnapshot,
                  }),
                session,
                threadId,
                timeoutMs: runOptions.timeoutMs,
              });
            },
            wasAccepted: Boolean,
          },
        );
        return result.kind === "already-dispatched" ? true : result.value;
      } catch (error) {
        return handleBrowserCommandError({
          command,
          error,
          commandGeneration,
          options: runOptions,
          useNativeBrowserCommand: commandPlan.commandRoute.shouldUseNativeBrowserCommand,
        });
      } finally {
        clearPendingBrowserCommand(pending);
      }
    },
    [
      activeTabRef,
      beginPendingBrowserCommand,
      clearPendingBrowserCommand,
      commandGenerationRef,
      defaultTimeoutMs,
      devTunnelRecordsRef,
      environmentId,
      projectId,
      handleBrowserCommandError,
      handleNativeBrowserSurfaceResult,
      onServerCommandResult,
      reportUnavailableBrowserCommand,
      session,
      setBrowserRuntimeError,
      threadId,
    ],
  );
}
